import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { get } from 'lodash';
import { CalendarClock, ChevronDown, ChevronUp } from 'lucide-react';
import CurrencyInput from '../../../../../components/CurrencyInput';

import { OrderSchemaType } from '../order.schema';
import { useOrderCalculations } from '../Hooks/useOrderCalculations';
import { computeDelivery, buildHolidayMessage } from '../utils/businessDays';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';

import {
  Title,
  FormGrid,
  FormGroup,
  Label,
  Input,
  CollapsibleHeader,
  CircularCollapseIcon,
  Separator,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
} from '../style';

import { HolidayChip, DerivedHint } from './ShippingSection.style';

import SteelOrdersInfiniteSelect from '../components/SteelOrdersInfiniteSelect';

const ShippingSection: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [holidayNotice, setHolidayNotice] = useState<string | null>(null);
  const { setFeedback } = useContext(FeedbackContext);
  const previewFailedRef = useRef(false);

  const { control, register, watch, setValue, getValues, formState: { errors } } = useFormContext<OrderSchemaType>();

  const { totalQuantity, taxes, practicedTotal } = useOrderCalculations();

  const deliveryDateRegistration = register('deliveryDate');
  const handleDeliveryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const previousDelivery = getValues('deliveryDate') || '';
    const currentBilling = getValues('billingDate') || '';
    deliveryDateRegistration.onChange(e);
    setHolidayNotice(null);
    const nextDelivery = e.target.value;
    if (nextDelivery && (!currentBilling || currentBilling === previousDelivery)) {
      setValue('billingDate', nextDelivery, { shouldDirty: true, shouldValidate: false });
    }
  };

  const applyBusinessDays = useCallback(async (days: number) => {
    if (isNaN(days) || days < 0) {
      setHolidayNotice(null);
      return;
    }
    const computation = await computeDelivery(getValues('orderDate'), days);
    if (computation.failed) {
      if (!previewFailedRef.current) {
        previewFailedRef.current = true;
        setFeedback({ message: 'Não foi possível calcular a previsão de entrega. Informe a data manualmente.', isOpen: true, statusOk: false });
      }
      setHolidayNotice(null);
      return;
    }
    previewFailedRef.current = false;
    if (computation.date) {
      const previousDelivery = getValues('deliveryDate') || '';
      const currentBilling = getValues('billingDate') || '';
      setValue('deliveryDate', computation.date, { shouldDirty: true, shouldValidate: false });
      if (!currentBilling || currentBilling === previousDelivery) {
        setValue('billingDate', computation.date, { shouldDirty: true, shouldValidate: false });
      }
    }
    setHolidayNotice(computation.skippedHolidays.length > 0 ? buildHolidayMessage(computation) : null);
  }, [getValues, setValue, setFeedback]);

  const businessDaysRegistration = register('deliveryBusinessDays', {
    setValueAs: (value) => value === '' || value === null ? undefined : Number(value),
  });
  const handleBusinessDaysChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    businessDaysRegistration.onChange(e);
    const raw = e.target.value;
    if (raw === '') {
      setHolidayNotice(null);
      return;
    }
    await applyBusinessDays(Number(raw));
  };

  const itemsWatch = useWatch({ control, name: 'items' }) as OrderSchemaType['items'] | undefined;
  const maxItemsBusinessDays = React.useMemo(() => {
    let max: number | undefined;
    (itemsWatch || []).forEach(item => {
      const raw = (item as any)?.deliveryBusinessDays;
      if (raw === undefined || raw === null || raw === '') return;
      const days = Number(raw);
      if (isNaN(days) || days <= 0) return;
      if (max === undefined || days > max) max = days;
    });
    return max;
  }, [itemsWatch]);

  const isDerivedFromItems = maxItemsBusinessDays !== undefined;

  useEffect(() => {
    if (maxItemsBusinessDays === undefined) return;
    if (Number(getValues('deliveryBusinessDays')) === maxItemsBusinessDays) return;
    setValue('deliveryBusinessDays', maxItemsBusinessDays, { shouldDirty: true, shouldValidate: false });
    applyBusinessDays(maxItemsBusinessDays);
  }, [maxItemsBusinessDays, getValues, setValue, applyBusinessDays]);

  return (
    <>
      <CollapsibleHeader onClick={() => setIsOpen(prev => !prev)}>
        <Title>Frete</Title>
        <CircularCollapseIcon onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}>
          {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </CircularCollapseIcon>
      </CollapsibleHeader>

      {isOpen && (
        <>
          <FormGrid>
            <FormGroup style={{ position: 'relative' }}>
              <Controller
                name="shippingTypeId"
                control={control}
                render={({ field }) => (
                  <SteelOrdersInfiniteSelect
                    label="Tipo de Frete"
                    endpoint="shipping-types"
                    items={field.value ? [{ id: field.value, name: watch('shippingTypeName') || 'Selecionado' }] : []}
                    setItems={(newItems: any) => {
                      field.onChange(newItems.length > 0 ? newItems[0].id : undefined);
                      setValue('shippingTypeName', newItems.length > 0 ? newItems[0].name : undefined, { shouldDirty: true });
                    }}
                    placeholder="Selecione"
                    searchPlaceholder="Buscar tipo..."
                  />
                )}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="shippingValue">Valor do Frete</Label>
              <Controller
                name="shippingValue"
                control={control}
                render={({ field }) => (
                  <CurrencyInput
                    id="shippingValue"
                    name={field.name}
                    value={field.value || 0}
                    onValueChange={(value, name, values) => {
                      field.onChange(values?.float || 0);
                    }}
                    placeholder="R$ 0,00"
                    hasError={!!get(errors, 'shippingValue')}
                  />
                )}
              />
            </FormGroup>

            <FormGroup>
              <Label>Código de rastreio</Label>
              <Input {...register('trackingCode')} placeholder="Digite" />
            </FormGroup>
          </FormGrid>

          <FormGrid columns={4}>
            <FormGroup>
              <Label htmlFor="orderDate">Data do Pedido</Label>
              <Input id="orderDate" type="date" {...register('orderDate')} hasError={!!get(errors, 'orderDate')} />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="deliveryBusinessDays">Prazo de entrega (dias úteis)</Label>
              <Input
                id="deliveryBusinessDays"
                type="number"
                min={0}
                {...businessDaysRegistration}
                onChange={handleBusinessDaysChange}
                placeholder={isDerivedFromItems ? 'Derivado dos itens' : 'Dias úteis'}
                disabled={isDerivedFromItems}
                hasError={!!get(errors, 'deliveryBusinessDays')}
              />
              {isDerivedFromItems && <DerivedHint>Maior prazo entre os itens</DerivedHint>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="deliveryDate">Previsão de entrega</Label>
              <Input id="deliveryDate" type="date" {...deliveryDateRegistration} onChange={handleDeliveryDateChange} hasError={!!get(errors, 'deliveryDate')} />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="billingDate">Previsão de faturamento</Label>
              <Input id="billingDate" type="date" {...register('billingDate')} hasError={!!get(errors, 'billingDate')} />
            </FormGroup>
          </FormGrid>

          {holidayNotice && (
            <HolidayChip>
              <CalendarClock size={14} />
              <span>{holidayNotice}</span>
            </HolidayChip>
          )}
        </>
      )}

      <Separator />

      <Summary>
        <SummaryItem>
          <SummaryLabel>Quantidade total:</SummaryLabel>
          <SummaryValue>{totalQuantity || 0}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Tributos:</SummaryLabel>
          <SummaryValue>
            {(taxes || 0).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
          </SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Total:</SummaryLabel>
          <SummaryValue>
            {(practicedTotal || 0).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            })}
          </SummaryValue>
        </SummaryItem>
      </Summary>
    </>
  );
};

export default ShippingSection;
